import { Component, Inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Store, select } from '@ngrx/store';

import { menuItems as siteMap } from './app-menu';
import * as coreModels from '../../models';
import * as sharedModels from '../../../shared/models';
import * as userAction from '../../actions/user.actions';
import * as layoutAction from '../../../shared/actions/layout.actions';
import * as apiStatusAction from '../../../shared/actions/api-status.actions';
import * as coreSelectors from '../../selectors';
import * as sharedSelectors from '../../../shared/selectors';
import { INotificationService, NOTIFICATION_SERVICE_TOKEN } from '../../../shared/types';
import { IGitEnvVariablesService, GIT_ENV_VARIABLES_SERVICE_TOKEN } from '../../../shared/services';
import {
  DefaultLayoutComponent as SharedDefaultLayoutComponent,
  MenuItem,
} from '../../../shared/components/';
import { pagesToggleService } from '../../../../assets/pages/services/toggler.service';

import { Observable, combineLatest } from 'rxjs';
import { filter, first, map, tap, take, delay } from 'rxjs/operators';
import * as _ from 'lodash';
import { AuthService, User } from "@auth0/auth0-angular";
import * as sharedTypes from '../../../shared/types';


declare let pg: any;

@Component({
  selector: 'default-layout',
  templateUrl: './default-layout.component.html',
})
export class DefaultLayoutComponent extends SharedDefaultLayoutComponent {
  user$: Observable<User>;
  activeRoute$: Observable<string>;
  menuItems$: Observable<Array<MenuItem>>;
  isDebug$: Observable<boolean>;
  accessDenied$: Observable<boolean>;
  menuPinThreeWay$: Observable<sharedModels.MenuPin>;
  environmentName$: Observable<string>;
  status$: Observable<sharedModels.ApiStatus>;
  isStatusLoading$: Observable<boolean>;
  gitEnvVariables$: Observable<sharedModels.GitEnvVariables>;
  token$: Observable<string>;
  private _notificationService: INotificationService;

  get isLoginPage() {
    return document.location.pathname === '/login';
  }

  constructor(
    public store$: Store<any>,
    public toggler: pagesToggleService,
    router: Router,
    @Inject(NOTIFICATION_SERVICE_TOKEN)
    notificationService: INotificationService,
    @Inject(GIT_ENV_VARIABLES_SERVICE_TOKEN)
    public gitEnvVariablesService: IGitEnvVariablesService,
    public auth: AuthService,
  ) {
    super(store$, router, toggler, auth);
    this._notificationService = notificationService;

    this.token$ = this.auth.getAccessTokenSilently();

    this.menuPinThreeWay$ = this.store$.pipe(
      select(sharedSelectors.getLayoutMenuPin),
      tap((pin) => {
        if (pin === 0) {
          if (pg.hasClass(document.body, 'menu-pin')) {
            pg.removeClass(document.body, 'menu-pin');
          }
          if (pg.hasClass(document.body, 'menu-pin-sm')) {
            pg.removeClass(document.body, 'menu-pin-sm');
          }
        } else if (pin === 1) {
          if (pg.hasClass(document.body, 'menu-pin') === false) {
            pg.addClass(document.body, 'menu-pin');
          }
          if (pg.hasClass(document.body, 'menu-pin-sm')) {
            pg.removeClass(document.body, 'menu-pin-sm');
          }
        } else {
          if (pg.hasClass(document.body, 'menu-pin') === false) {
            pg.addClass(document.body, 'menu-pin');
          }
          if (pg.hasClass(document.body, 'menu-pin-sm') === false) {
            pg.addClass(document.body, 'menu-pin-sm');
          }
        }
      })
    );

    this.isDebug$ = this.store$.pipe(select(sharedSelectors.getSystemIsDebug));
    this.accessDenied$ = this.store$.pipe(select(coreSelectors.getUserUserIsAccessDenied));

    this.setNotificationService(notificationService);

    // Override
    this.isFullHeightView$ = combineLatest([
      this.store$.pipe(select(sharedSelectors.getLayoutState)),
      this.store$.pipe(select(coreSelectors.getUserUserIsAccessDenied)),
      this.store$.pipe(select(sharedSelectors.getSystemState)),
    ]).pipe(
      delay(0), // Delay to ensure that the layout state has been updated before evaluating the full height view
      map(([layoutState, userAccessDenied, systemState]) => {
        return (
          layoutState.isAppView ||
          layoutState.isLoading ||
          layoutState.isFullHeightView ||
          layoutState.isError ||
          userAccessDenied ||
          systemState.browserSupported === false
        );
      })
    );

    // Override
    this.outletIsVisible$ = combineLatest([
      this.store$.pipe(select(sharedSelectors.getLayoutState)),
      this.store$.pipe(select(coreSelectors.getUserUserIsAccessDenied)),
      this.store$.pipe(select(sharedSelectors.getSystemState)),
    ]).
    pipe(
      delay(0),
      map(([layoutState, userAccessDenied, systemState]) => {
        return (
          layoutState.isLoading === false &&
          layoutState.isError === false &&
          userAccessDenied === false &&
          systemState.browserSupported
        );
      })
    );

    this.user$ = this.auth.user$;
    this.activeRoute$ = this.store$.pipe(select(coreSelectors.getRouterPath));

    this.menuItems$ = combineLatest([
      this.store$.pipe(select(coreSelectors.getUserUser)),
      this.store$.pipe(select(sharedSelectors.getSystemIsDebug)),
    ]).pipe(
      filter(([user]) => !!user),
      map(([user, isDebugMode]) =>
        _.cloneDeep(siteMap).filter((smi) =>
          this.menuItemFilter(smi, isDebugMode, user.viewPermissions)
        )
      )
    );
    this.environmentName$ = this.store$.pipe(
      select(sharedSelectors.getLayoutServerEnvironment),
      map((env) => (env === 'prod' ? '' : 'Develop'))
    );

    this.status$ = this.store$.pipe(select(sharedSelectors.getApiStatus));
    this.isStatusLoading$ = this.store$.pipe(select(sharedSelectors.getApiStatusIsLoading));
    this.gitEnvVariables$ = this.gitEnvVariablesService.getGitEnvVariables();

    if (!this.isLoginPage) {
      const menuPin = Number(localStorage.getItem('menuPin')) || 0;
      this.setLayoutMenuPin(menuPin);
      router.events.pipe(
        filter((event) => event instanceof NavigationEnd && event.url === '/login'),
        take(1)
      )
        .subscribe(() => {
          this.setLayoutMenuPin(0);
        })
    }
  }

  copyToClipboard(value: string, message: string): void {
    navigator.clipboard
      .writeText(value)
      .then(() => {
        this._notificationService.show(
          message,
          sharedTypes.NotificationStyle.Bar,
          sharedTypes.NotificationType.Success,
          sharedTypes.NotificationPosition.Top,
          1000
        );
      })
      .catch(() => {
        this._notificationService.show(
          'Copy Failed',
          sharedTypes.NotificationStyle.Bar,
          sharedTypes.NotificationType.Danger,
          sharedTypes.NotificationPosition.Top,
          1000
        );
      });
  }

  onCopyTokenClick(token: string): void {
    this.copyToClipboard(token, 'Token Copied to Clipboard');
  }

  // Override
  async toggleMenuPin() {
    if (pg.isVisibleSm()) {
      this.setLayoutMenuPin(sharedModels.MenuPin.CLOSED);
      return;
    }
    let menuPin = await this.menuPinThreeWay$.pipe(first()).toPromise();
    menuPin = (menuPin + 1) % 3;
    localStorage.setItem('menuPin', menuPin.toString());
    this.setLayoutMenuPin(menuPin);
  }

  setLayoutMenuPin(pin: sharedModels.MenuPin): void {
    this.store$.dispatch(layoutAction.setMenuPin({ payload: pin }));
    this.store$.dispatch(userAction.setMenuPinPreference({ payload: pin }));
  }

  menuItemFilter(mi: MenuItem, isDebug: boolean, viewPermissions: coreModels.IPermissions): boolean {
    if (_.get(mi, 'submenu', false)) {
      mi.submenu = mi.submenu.filter((sm) => this.menuItemFilter(sm, isDebug, viewPermissions));
    }

    let dontRemove = false;

    // If Menu Item doesnt have viewPermission Setting
    // Then check if Debug
    // If Menu Item has viewPermission Setting
    // Then check if the viewPermission is true before checking for Debug mode only
    if (!_.get(mi, 'viewPermission', false)) {
      dontRemove = true;

      if (_.get(mi, 'debugOnly', false)) {
        // Is for Debug only
        dontRemove = isDebug === true;
      }
    } else if (_.castArray(mi.viewPermission).some((perm) => _.get(viewPermissions, perm, false))) {
      dontRemove = true;

      if (_.get(mi, 'debugOnly', false)) {
        // Is for Debug only
        dontRemove = isDebug === true;
      } else {
        dontRemove = true;
      }
    }

    return dontRemove;
  }

  onApiStatusExpand(apiName: string): void {
    this.store$.dispatch(apiStatusAction.loadApiStatus({ payload: apiName }));
  }
}
